import { useEffect, useReducer } from 'react';

export const dcfInitialState = {
    baseRevenue: null,
    stage1Years: 5,
    stage1Growth: 0.05,
    stage2Years: 5,
    stage2Growth: 0.03,
    fcfGrowthRate: 0.05,
    startingFcf: null,
    operatingMarginStart: 0.15,
    operatingMarginTarget: 0.15,
    taxRate: 0.21,
    dAndAPctRevenue: 0.03,
    capexPctRevenue: 0.04,
    nwcPctRevenueChange: 0.02,
    wacc: 0.09,
    terminalGrowth: 0.025,
    forecastYears: 5,
    netDebt: null,
    sharesOutstanding: null,
    exitMultiple: 10,
    totalDebt: null,
    preferredStock: 0,
    noncontrollingInterest: 0,
    cash: null,
    projections: null,
    projectionsCustomized: false,
    actuals: null,
    actualsCustomized: false,
    seeded: false,
};

const PROJECTION_FIELDS = new Set(['revenue', 'ebitda', 'depreciation', 'taxes', 'capex', 'deltaNwc']);
const ACTUAL_FIELDS = PROJECTION_FIELDS;

function finiteOrFallback(value, fallback) {
    if (value == null || value === '') return fallback;
    return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

function capitalStructureFromSources(action) {
    const debt = finiteOrFallback(action.totalDebt?.value, null);
    const cash = finiteOrFallback(action.cash?.value, null);
    const netDebt = finiteOrFallback(action.netDebt?.value, null);
    if (debt !== null || cash !== null) {
        return {
            totalDebt: debt ?? 0,
            cash: cash ?? 0,
        };
    }
    if (netDebt === null) return { totalDebt: null, cash: null };
    if (netDebt < 0) return { totalDebt: 0, cash: -netDebt };
    return { totalDebt: netDebt, cash: 0 };
}

function resizeProjections(projections, years) {
    if (!Array.isArray(projections) || years === projections.length) return projections;
    if (years < projections.length) {
        return projections.slice(0, years).map((row, index) => ({ ...row, year: index + 1 }));
    }
    const last = projections[projections.length - 1];
    const next = projections.map((row, index) => ({ ...row, year: index + 1 }));
    for (let year = projections.length + 1; year <= years; year += 1) {
        next.push({ ...last, year });
    }
    return next;
}

export function dcfReducer(state, action) {
    switch (action.type) {
        case 'seed_defaults': {
            if (state.seeded && !action.force) return state;
            const growth = finiteOrFallback(action.defaults?.revenue_growth, state.stage1Growth);
            const margin = finiteOrFallback(action.defaults?.operating_margin, state.operatingMarginStart);
            const structure = capitalStructureFromSources(action);
            const reportedNetDebt = finiteOrFallback(action.netDebt?.value, null);
            const netDebt = reportedNetDebt ?? (
                structure.totalDebt != null && structure.cash != null
                    ? structure.totalDebt - structure.cash
                    : null
            );
            return {
                ...state,
                baseRevenue: finiteOrFallback(action.baseRevenue?.value, state.baseRevenue),
                stage1Growth: growth,
                stage2Growth: growth,
                fcfGrowthRate: growth,
                operatingMarginStart: margin,
                operatingMarginTarget: margin,
                netDebt: netDebt ?? state.netDebt,
                sharesOutstanding: finiteOrFallback(action.shares?.value, state.sharesOutstanding),
                ...structure,
                seeded: true,
            };
        }
        case 'set_field': {
            if (action.field === 'fcfGrowthRate') {
                return {
                    ...state,
                    fcfGrowthRate: action.value,
                    stage1Growth: action.value,
                    stage2Growth: action.value,
                };
            }
            if (action.field === 'forecastYears' && state.projectionsCustomized) {
                const years = Math.round(Number(action.value));
                if (Number.isFinite(years) && years >= 1) {
                    return {
                        ...state,
                        forecastYears: years,
                        projections: resizeProjections(state.projections, years),
                    };
                }
            }
            return { ...state, [action.field]: action.value };
        }
        case 'sync_forecast_rows':
            if (state.projectionsCustomized || !Array.isArray(action.rows)) return state;
            return { ...state, projections: action.rows };
        case 'set_projection_field': {
            if (!PROJECTION_FIELDS.has(action.field) || !Number.isFinite(Number(action.value))) return state;
            const base = Array.isArray(state.projections) && state.projections.length
                ? state.projections
                : action.fallbackRows;
            if (!Array.isArray(base) || base.length === 0) return state;
            return {
                ...state,
                projectionsCustomized: true,
                projections: base.map((row) => (
                    Number(row.year) === Number(action.year)
                        ? { ...row, [action.field]: Number(action.value) }
                        : row
                )),
            };
        }
        case 'set_actual_field': {
            if (!ACTUAL_FIELDS.has(action.field) || !Number.isFinite(Number(action.value))) return state;
            const base = Array.isArray(state.actuals) && state.actuals.length
                ? state.actuals
                : action.fallbackRows;
            if (!Array.isArray(base) || base.length === 0) return state;
            return {
                ...state,
                actualsCustomized: true,
                actuals: base.map((row) => (
                    Number(row.year) === Number(action.year)
                        ? { ...row, [action.field]: Number(action.value) }
                        : row
                )),
            };
        }
        case 'reset_projections':
            return {
                ...state,
                projectionsCustomized: false,
                projections: Array.isArray(action.rows) ? action.rows : null,
                actualsCustomized: false,
                actuals: null,
            };
        case 'set_starting_fcf_default':
            if (state.startingFcf != null || !Number.isFinite(Number(action.value))) return state;
            return { ...state, startingFcf: action.value };
        case 'load_scenario': {
            if (!action.assumptions || typeof action.assumptions !== 'object') return state;
            const loaded = {};
            Object.keys(dcfInitialState).forEach((field) => {
                if (field !== 'seeded' && Object.hasOwn(action.assumptions, field)) {
                    loaded[field] = action.assumptions[field];
                }
            });
            if (!Object.hasOwn(loaded, 'fcfGrowthRate') && Object.hasOwn(loaded, 'stage1Growth')) {
                loaded.fcfGrowthRate = loaded.stage1Growth;
            }
            const next = { ...state, ...loaded, seeded: true };
            if (Array.isArray(loaded.projections) && loaded.projections.length > 0) {
                next.projectionsCustomized = loaded.projectionsCustomized ?? true;
                next.forecastYears = loaded.projections.length;
            } else {
                next.projections = null;
                next.projectionsCustomized = false;
            }
            if (Array.isArray(loaded.actuals) && loaded.actuals.length > 0) {
                next.actualsCustomized = loaded.actualsCustomized ?? true;
            } else {
                next.actuals = null;
                next.actualsCustomized = false;
            }
            if (!Object.hasOwn(loaded, 'exitMultiple')) next.exitMultiple = dcfInitialState.exitMultiple;
            if (!Object.hasOwn(loaded, 'preferredStock')) next.preferredStock = 0;
            if (!Object.hasOwn(loaded, 'noncontrollingInterest')) next.noncontrollingInterest = 0;
            if (!Object.hasOwn(loaded, 'totalDebt') && Object.hasOwn(loaded, 'netDebt')) {
                const netDebt = finiteOrFallback(loaded.netDebt, null);
                if (netDebt !== null) {
                    next.totalDebt = netDebt > 0 ? netDebt : 0;
                    if (!Object.hasOwn(loaded, 'cash')) next.cash = netDebt < 0 ? -netDebt : 0;
                }
            }
            return next;
        }
        case 'apply_average':
            if (!Number.isFinite(action.value)) return state;
            if (action.driver === 'revenue_growth') {
                return {
                    ...state,
                    stage1Growth: action.value,
                    stage2Growth: action.value,
                    fcfGrowthRate: action.value,
                };
            }
            if (action.driver === 'operating_margin') {
                return {
                    ...state,
                    operatingMarginStart: action.value,
                    operatingMarginTarget: action.value,
                };
            }
            return state;
        default:
            return state;
    }
}

export function useDcfController({ defaults, baseRevenue, netDebt, totalDebt, cash, shares }) {
    const [state, dispatch] = useReducer(dcfReducer, dcfInitialState);

    useEffect(() => {
        const hasDefaults = defaults && Object.keys(defaults).length > 0;
        const hasSourceValue = [baseRevenue, netDebt, totalDebt, cash, shares]
            .some((source) => source?.value != null);
        if (!hasDefaults && !hasSourceValue) return;
        dispatch({
            type: 'seed_defaults',
            defaults,
            baseRevenue,
            netDebt,
            totalDebt,
            cash,
            shares,
        });
    }, [defaults, baseRevenue, netDebt, totalDebt, cash, shares]);

    return { state, dispatch };
}
